import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react'

/**
 * useState, но значение сохраняется в localStorage под ключом `key`.
 * Нужен, чтобы фильтры куратора (ай/апта, курс, группа) не сбрасывались при
 * переходе на страницу ученика и обратно. Один и тот же ключ на разных
 * страницах = общий выбор периода.
 */
export function usePersistedState<T>(
    key: string,
    initial: T | (() => T),
): [T, Dispatch<SetStateAction<T>>] {
    const [state, setState] = useState<T>(() => {
        try {
            const raw = localStorage.getItem(key)
            if (raw != null && raw !== 'undefined') {
                return JSON.parse(raw) as T
            }
        } catch {
            /* повреждённое значение — берём initial */
        }
        return typeof initial === 'function' ? (initial as () => T)() : initial
    })

    // Ключ обычно стабилен; ref, чтобы эффект не гонялся из-за смены ссылки
    const keyRef = useRef(key)
    keyRef.current = key

    useEffect(() => {
        try {
            if (state === undefined) {
                localStorage.removeItem(keyRef.current)
            } else {
                localStorage.setItem(keyRef.current, JSON.stringify(state))
            }
        } catch {
            /* приватный режим / переполнение — молча пропускаем */
        }
    }, [state])

    return [state, setState]
}

export default usePersistedState
